import { useEffect, useState } from 'react';
import { Loader2, MailOpen, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api
      .get('/admin/contacts')
      .then(({ data }) => setMessages(data))
      .catch(() => toast.error('No se pudieron cargar los mensajes'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const markRead = async (messageId) => {
    try {
      await api.patch(`/admin/contacts/${messageId}`);
      setMessages(messages.map((m) => (m.message_id === messageId ? { ...m, status: 'read' } : m)));
    } catch {
      toast.error('No se pudo marcar como leído.');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Buzón</p>
        <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <MessageSquare className="h-7 w-7 text-cnib-blue" /> Mensajes de contacto
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
        </div>
      ) : messages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500" data-testid="admin-messages-empty">
          No hay mensajes recibidos todavía.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="admin-messages-list">
          {messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`rounded-2xl border bg-white p-6 ${
                msg.status === 'new' ? 'border-orange-500/30 shadow-sm shadow-orange-500/10' : 'border-slate-200'
              }`}
              data-testid={`admin-message-${msg.message_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-bold text-slate-900">{msg.subject}</p>
                    {msg.status === 'new' && (
                      <span className="rounded-full bg-orange-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-orange-600">
                        Nuevo
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {msg.name} · {msg.email} {msg.phone ? `· ${msg.phone}` : ''}
                  </p>
                  <p className="text-xs text-slate-400">
                    {new Date(msg.created_at).toLocaleDateString('es-BO', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                {msg.status === 'new' && (
                  <button
                    onClick={() => markRead(msg.message_id)}
                    data-testid={`admin-message-read-${msg.message_id}`}
                    className="flex items-center gap-1.5 rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-200"
                  >
                    <MailOpen className="h-3.5 w-3.5" /> Marcar leído
                  </button>
                )}
              </div>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
